import { Button, Stack, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import feedbackService from "../../api/FeedbackService";
import { useSnackbar } from "../../common/context/SnackbarProvider";
import { borderStyle, iconSize, smallGap } from "../../styles/consts";
import CommentsPanel from "./Feedback/CommentsPanel";
import RatingScale from "./Feedback/RatingScale";

const ratingScaleDescriptions = {
  Match: {
    title: "How would you rate this offer to your profile personally?",
    description: `Rate how well this job offer matches your skills and experience. This is the rating of how 
      would you personally rate this offer to your profile and CV. For example if the language German is required 
      in the offer but you don't speak it, don't give it a 1 immediately but rather look at it broadly 
      and consider other factors as well.`,
  },
  CV: {
    title: "How good is the CV?",
    description: "Rate the quality and effectiveness of the CV",
  },
  Email: {
    title: "How good is the email?",
    description: "Rate the clarity and professionalism of the email",
  },
};

const initialRatings = Object.keys(ratingScaleDescriptions).reduce(
  (acc, key) => {
    acc[key] = undefined;
    return acc;
  },
  {}
);

export default function AlqemiFeedback({
  displayedMatchId,
  onFeedbackSubmitted,
  refreshTrigger = 0,
}) {
  const [feedbackText, setFeedbackText] = useState("");
  const [ratings, setRatings] =
    useState<Record<string, number | undefined>>(initialRatings);

  const { showSnackbar } = useSnackbar();

  const handleRatingChange = (title: string, value: number | undefined) => {
    setRatings((prev) => ({
      ...prev,
      [title]: value,
    }));
  };

  const hasUnsavedChanges = () => {
    if (feedbackText.trim().length > 0) return true;

    for (const key in ratings) {
      if (ratings[key] !== undefined) return true;
    }

    return false;
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges()) {
        const confirmationMessage =
          "You have unsaved feedback. Are you sure you want to leave this page?";
        e.preventDefault();
        e.returnValue = confirmationMessage;
        return confirmationMessage;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [feedbackText, ratings]);

  const handleSubmitFeedback = async () => {
    await feedbackService.createFeedback({
      matchId: displayedMatchId,
      feedback: feedbackText,
      rating: ratings,
      offerUrl: window.location.href,
    });
    setFeedbackText("");
    setRatings(initialRatings);
    showSnackbar("Feedback sent successfully", false, true);
    onFeedbackSubmitted();
  };

  return (
    <Stack
      sx={{
        border: borderStyle,
        padding: iconSize,
      }}
    >
      <Stack
        sx={{
          paddingRight: smallGap,
          overflowY: "auto",
        }}
      >
        <CommentsPanel
          displayedMatchId={displayedMatchId}
          refreshTrigger={refreshTrigger}
        />
        <Stack
          sx={{
            flexDirection: "row",
            width: "100%",
            gap: "2rem",
            alignItems: "center",
          }}
        >
          <Stack sx={{ gap: smallGap, width: "50%" }}>
            <TextField
              placeholder="Feedback"
              sx={{
                width: "100%",
              }}
              autoComplete="off"
              multiline
              rows={7}
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
            />
            <Button
              variant="primary"
              onClick={handleSubmitFeedback}
              disabled={!hasUnsavedChanges()}
            >
              Submit Feedback
            </Button>
          </Stack>

          <Stack
            sx={{
              justifyContent: "center",
            }}
          >
            {Object.keys(ratings).map((title) => (
              <RatingScale
                key={title}
                title={ratingScaleDescriptions[title].title}
                description={ratingScaleDescriptions[title].description}
                rating={ratings[title]}
                setRating={(value) => handleRatingChange(title, value)}
              />
            ))}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}
